/**
 * What the app says when somebody comes back from Google or Apple.
 *
 * **It lives in the shell, not in the sign-in sheet.** The callback redirects to
 * the app ROOT, and the sheet is closed there — so the first version of this
 * read `?signin=` inside `SignInOptions` and a person waiting for approval was
 * returned to a normal-looking app that simply never signed them in. Nothing
 * was shown, nothing was wrong on screen, and the only evidence was a query
 * parameter nobody rendered.
 *
 * The outcome is consumed once and the parameter stripped: a reload must not
 * re-announce a sign-in from ten minutes ago, and the address bar should not
 * carry it into a bookmark or a shared link.
 *
 * `ok` and `created` render nothing at all. The session is already in an
 * httpOnly cookie and `AuthProvider` has exchanged it on boot — a person who is
 * signed in wants the app, not a notice saying they are signed in.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from '@/lib/api';
import * as storage from '@/lib/storage';
import { Button, Card, Row } from '@/ui';
import { Field, Alert } from '@/visitor/authControls';
import { afterSignIn } from './ReturnAfterSignIn';
import { useAuth } from './AuthProvider';
import { platformDomain } from '@/lib/tenant';
import { bannedMessage } from './platform';
import { useOneTimeCode } from '@/visitor/useOneTimeCode';
import { useT } from '@/i18n';
import './SignInOutcome.css';

/**
 * **`pending` is deliberately not one of these** (`plan.md` §6.12).
 *
 * The callback used to send it for an account waiting on an admin, and this
 * file drew a whole screen for it. An unapproved account now holds a session,
 * so `RequireApproval` in the shell draws that screen from the TOKEN — which
 * means it survives a reload, and an outcome cookie consumed once cannot.
 * Keeping both gave one account two different screens depending on whether it
 * had been refreshed, and this one's sentence — "before it can sign in" — had
 * stopped being true.
 */
type Outcome = 'ok' | 'created' | 'cancelled' | 'error' | 'expired' | 'closed' | 'banned' | LinkOutcome;

/**
 * Coming back from LINKING a provider to a signed-in account (`plan.md`
 * §6.10) — not from a sign-in. No session changed hands, so these are a
 * notice over the app rather than a screen instead of it.
 */
type LinkOutcome = 'linked' | 'link-taken' | 'link-expired';
const LINK_OUTCOMES: readonly string[] = ['linked', 'link-taken', 'link-expired'];

/**
 * Which provider a link was started for, written by the account page just
 * before it leaves for the provider. The outcome cookie carries only a word,
 * and "Google is now linked" is a better sentence than "a method is".
 */
export const LINKING_KEY = 'linking-provider';

const PROVIDER_NAMES: Record<string, string> = {
  GOOGLE: 'Google', APPLE: 'Apple', FACEBOOK: 'Facebook',
};

/**
 * `link:<id>` — the address belongs to an account that is not this sign-in
 * method's yet, and that account has been sent a code.
 */
const LINK_PREFIX = 'link:';

/**
 * `link-confirm:<id>` — a provider came back from LINKING and the server parked
 * it (step 70 review S3). This page confirms it with its own session; the
 * server attaches it only when that session is the account that asked.
 */
const LINK_CONFIRM_PREFIX = 'link-confirm:';

const COOKIE = 'myrave_signin';
const KNOWN: readonly string[] = ['ok', 'created', 'cancelled', 'error', 'expired', 'closed', 'banned', ...LINK_OUTCOMES];

/**
 * Read the outcome cookie once, delete it, and forget it.
 *
 * **A cookie and not a query parameter.** `?signin=ok&provider=GOOGLE` was
 * visible in the address bar, survived into the history, and went into any link
 * the person copied — and stripping it in an effect happens after first paint,
 * so it flashed regardless. The API writes a two-minute cookie scoped to the
 * platform domain instead, and the URL the browser lands on is just `/`.
 *
 * Deleted immediately: a reload must not re-announce a sign-in, and a tab
 * opened later must not inherit one.
 */
function useOutcome(): { outcome: Outcome | null; linkId: string | null; parkedId: string | null } {
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [linkId, setLinkId] = useState<string | null>(null);
  const [parkedId, setParkedId] = useState<string | null>(null);

  useEffect(() => {
    const value = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${COOKIE}=`))
      ?.slice(COOKIE.length + 1);
    if (!value) return;

    /*
     * Clear it on every host it could have been written for.
     *
     * **`slice(-2)` was a guess and it was wrong.** The API writes this cookie
     * twice — host-only and `Domain=<PLATFORM_DOMAIN>` — and this took the
     * last TWO labels of the hostname as the platform domain. That is right
     * for `myrave.io` and wrong for anything deeper: on `dev.myrave.io` it
     * tried to clear `myrave.io`, matched nothing, and the cookie survived.
     *
     * What that looked like: confirm a link, get signed in, and land back on
     * the same screen — because the stale cookie re-announced an outcome that
     * had already happened, with a `linkId` now consumed, so the code was
     * refused on every retry. The sign-in had worked; only the message would
     * not go away.
     *
     * `platformDomain()` is the same value the API used, so this no longer
     * guesses. The parent suffixes stay as a belt-and-braces for a cookie
     * written by an older build under a different scope.
     */
    const host = window.location.hostname;
    const labels = host.split('.');
    const parents = labels.map((_, i) => labels.slice(i).join('.')).filter((d) => d.includes('.'));
    const domains = [undefined, host, platformDomain(), ...parents];
    for (const domain of new Set(domains)) {
      document.cookie =
        `${COOKIE}=; Path=/; Max-Age=0` + (domain ? `; Domain=${domain}` : '');
    }

    if (value.startsWith(LINK_PREFIX)) {
      setLinkId(value.slice(LINK_PREFIX.length));
      return;
    }
    if (value.startsWith(LINK_CONFIRM_PREFIX)) {
      setParkedId(value.slice(LINK_CONFIRM_PREFIX.length));
      return;
    }
    setOutcome((KNOWN.includes(value) ? value : 'error') as Outcome);
  }, []);

  return { outcome, linkId, parkedId };
}

/**
 * Confirm a parked link once the session is known, then say what happened as
 * any link outcome. No session, or another account's, reads "expired".
 */
function useParkedLink(parkedId: string | null): LinkOutcome | null {
  const { user, loading } = useAuth();
  const [result, setResult] = useState<LinkOutcome | null>(null);
  const sent = useRef(false);
  useEffect(() => {
    if (!parkedId || loading || sent.current) return;
    sent.current = true;
    if (!user) { setResult('link-expired'); return; }
    api.post<{ outcome: string }>('/me/auth-methods/confirm', { id: parkedId })
      .then(({ outcome }) => setResult(
        outcome === 'linked' || outcome === 'already-linked' ? 'linked' : outcome === 'taken' ? 'link-taken' : 'link-expired',
      ))
      .catch(() => setResult('link-expired'));
  }, [parkedId, loading, user]);
  return result;
}

export function SignInOutcome({ children }: { children: ReactNode }) {
  const { outcome: said, linkId, parkedId } = useOutcome();
  const parked = useParkedLink(parkedId);
  const outcome = parked ?? said;

  // The address belongs to an account this method is not attached to yet. That
  // account has been sent a code, and holding it is the proof.
  if (linkId) return <ConfirmLink linkId={linkId} />;

  // Signed in, or nothing happened. Either way the app is what they want.
  if (!outcome || outcome === 'ok' || outcome === 'created') return <>{children}</>;

  if (outcome === 'cancelled') return <>{children}</>;

  if (LINK_OUTCOMES.includes(outcome)) {
    return <LinkNotice outcome={outcome as LinkOutcome}>{children}</LinkNotice>;
  }

  /*
   * A FIRST sign-in with Google or Apple while registration is closed. The
   * provider said yes and we said no — "that did not finish" would send them
   * round the same loop, so it gets its own sentence.
   */
  if (outcome === 'closed') return <RegistrationClosedScreen />;

  /*
   * The provider account, or the address it carries, is banned (the user,
   * 2026-10-07, decision 7). "Try again" would be a lie, so this says why.
   */
  if (outcome === 'banned') return <BannedScreen />;

  /*
   * `error` and `expired` read the same to the person in front of it: the
   * sign-in did not finish and nothing was saved. The distinction is in the
   * server's logs, where it is useful, and not on a screen whose only action
   * is "try again" either way.
   */
  return <Standalone />;
}

/**
 * "You already have an account — confirm it's you."
 *
 * A full screen, because it is the whole of what this person can do. The code
 * went to the email or phone the EXISTING account holds, which is what makes
 * this safe: the provider's claim about the address is not trusted, and does
 * not need to be.
 */
function ConfirmLink({ linkId }: { linkId: string }) {
  const tr = useT();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filled from the SMS where the browser can — see `useOneTimeCode`.
  useOneTimeCode(setCode);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/link/confirm', { linkId, code });
      /*
       * `app.`, not `/`.
       *
       * A full navigation, because the session arrives as a cookie the app
       * exchanges on boot — booting is the honest way to pick it up. And to
       * the APP host specifically: `/` reloads whichever host showed this
       * prompt, which is `login.` when the sign-in started there, and
       * `LoginPage` has no already-signed-in branch — so somebody who had just
       * linked their account landed back on a password form.
       */
      window.location.href = afterSignIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tr('That code is not right.'));
      setBusy(false);
    }
  }

  return (
    <main className="app-outcome">
      <form onSubmit={submit} className="app-outcome-body is-form">
        <h1 className="app-title">
          {tr('You already have an account')}
        </h1>
        <p className="app-lede">
          {tr('That address is already registered here. We have sent a six-digit code to the email or phone on that account — enter it and this new way of signing in will be added to it, so everything stays in one place.')}
        </p>

        <Field
          label={tr('Code')}
          type="text"
          value={code}
          onChange={(v) => setCode(v.replace(/\D/g, ''))}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
          dir="ltr"
          className="sign-in-outcome-code-input"
        />

        {error && <Alert>{error}</Alert>}

        <Button type="submit" disabled={busy || code.length !== 6} variant="primary" full>
          {busy ? tr('One moment…') : tr('Confirm and sign in')}
        </Button>

        <button
          type="button"
          onClick={() => { window.location.href = '/'; }}
          className="app-link-quiet"
        >
          {tr('Not now')}
        </button>
      </form>
    </main>
  );
}

/**
 * A full screen rather than a banner.
 *
 * A sign-in that did not finish is the whole of where this person is, not a
 * detail beside an app they cannot use yet. It also once carried the "waiting
 * for approval" case, which has moved to `RequireApproval` — see `Outcome`.
 *
 * A whole-page outcome: a title, what happened, one way on.
 *
 * The way on is a full reload, not a router navigation. The session — if
 * there is one — arrives through a cookie exchange that happens when the app
 * boots, so the honest way back is to boot it.
 */
function OutcomePage({ title, body, action }: { title: string; body: string; action: string }) {
  return (
    <main className="app-outcome">
      <div className="app-outcome-body">
        <h1 className="app-title">{title}</h1>
        <p className="app-lede">{body}</p>
        <button type="button" onClick={() => { window.location.href = '/'; }} className="app-outcome-go">
          {action}
        </button>
      </div>
    </main>
  );
}

function Standalone() {
  const tr = useT();
  return (
    <OutcomePage
      title={tr('That did not finish')}
      body={tr('Something went wrong on the way back from the sign-in provider. Nothing was saved and nothing was charged — try again, or use an email and password instead.')}
      action={tr('Try again')}
    />
  );
}

/**
 * Registration is closed, and the provider's account had none here yet. It
 * says plainly that nothing was created — a person who just consented on
 * Google's screen will otherwise assume an account now exists somewhere.
 */
function RegistrationClosedScreen() {
  const tr = useT();
  return (
    <OutcomePage
      title={tr('Registration is closed')}
      body={tr('New accounts are not being created right now, so no account was made for that sign-in. If you already have an account, you can still sign in to it the way you usually do.')}
      action={tr('Back')}
    />
  );
}

/** Banned: nothing was made and nothing was signed in. */
function BannedScreen() {
  const tr = useT();
  return (
    <OutcomePage
      title={tr('This sign-in cannot be used')}
      body={bannedMessage(tr)}
      action={tr('Back')}
    />
  );
}

/**
 * What a returning link says, over the app it returned to.
 *
 * **`link-taken` never names the other account.** Saying whose it is would
 * turn this into a way to find out who owns a Google address.
 */
export function linkOutcomeMessage(
  outcome: LinkOutcome,
  provider: string | null,
  tr: (english: string, vars?: Record<string, string>) => string,
): string {
  const name = (provider && PROVIDER_NAMES[provider]) || null;
  switch (outcome) {
    case 'linked':
      return name
        ? tr('{provider} is now linked. You can sign in with it from now on.', { provider: name })
        : tr('That sign-in method is now linked. You can sign in with it from now on.');
    case 'link-taken':
      return name
        ? tr('That {provider} account is already used by another MyRave account, so it was not linked. Nothing on your account changed.', { provider: name })
        : tr('That account is already used by another MyRave account, so it was not linked. Nothing on your account changed.');
    case 'link-expired':
      return tr('The link expired before it finished — try again from your account page.');
  }
}

function LinkNotice({ outcome, children }: { outcome: LinkOutcome; children: ReactNode }) {
  const [provider] = useState<string | null>(() => {
    const p = storage.readJson<string | null>(null, LINKING_KEY, null);
    storage.remove(null, LINKING_KEY);
    return p;
  });
  const tr = useT();
  const [open, setOpen] = useState(true);
  const good = outcome === 'linked';

  return (
    <>
      {open && (
        <div className="app-notice-wrap">
          <Card className={good ? 'sign-in-outcome-notice is-good' : 'sign-in-outcome-notice'}>
            <Row gap={10} className="ui-between">
              <span role={good ? 'status' : 'alert'} className="app-banner-text">
                {linkOutcomeMessage(outcome, provider, tr)}
              </span>
              <Button variant="quiet" onClick={() => setOpen(false)}>{tr('Dismiss')}</Button>
            </Row>
          </Card>
        </div>
      )}
      {children}
    </>
  );
}

